import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  X,
  ChevronLeft,
  ChevronRight,
  ArrowLeft,
  Image as ImageIcon
} from "lucide-react";
import { BUSINESS, TRANSLATIONS } from "@/config/business";

export default function Gallery({ lang }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("all");
  const [lightboxIndex, setLightboxIndex] = useState(null);
  
  const t = (key) => TRANSLATIONS[lang][key] || key;

  useEffect(() => {
    // Fetch metadata from the public folder dynamically at runtime
    fetch("/images/gallery/metadata.json")
      .then((res) => {
        if (!res.ok) {
          throw new Error("Metadata file not found");
        }
        return res.json();
      })
      .then((data) => {
        setImages(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Error loading gallery metadata:", err);
        setLoading(false);
      });
  }, []);

  // Filter images based on selected category
  const filteredImages = activeCategory === "all"
    ? images
    : images.filter(img => img.category.toLowerCase() === activeCategory.toLowerCase());

  // Get unique list of categories present in metadata
  const categories = ["all", ...new Set(images.map(img => img.category.toLowerCase()))];

  const getCategoryLabel = (cat) => {
    if (cat === "all") return t("allImages");
    if (cat === "general") return lang === "en" ? "General" : "सामान्य";
    if (cat === "studio") return t("categoryStudio");
    if (cat === "equipment") return t("categoryEquipment");
    if (cat === "sessions") return t("categorySessions");
    return cat.charAt(0).toUpperCase() + cat.slice(1);
  };

  // Lightbox functions
  const openLightbox = (index) => {
    // Find index of clicked image in filtered list
    setLightboxIndex(index);
    document.body.style.overflow = "hidden"; // Prevent scrolling behind modal
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
    document.body.style.overflow = "unset";
  };

  const navigateLightbox = (direction, e) => {
    e.stopPropagation();
    if (lightboxIndex === null) return;
    
    let newIndex = lightboxIndex + direction;
    if (newIndex < 0) {
      newIndex = filteredImages.length - 1;
    } else if (newIndex >= filteredImages.length) {
      newIndex = 0;
    }
    setLightboxIndex(newIndex);
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (lightboxIndex === null) return;
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowRight") setLightboxIndex((prev) => (prev + 1) % filteredImages.length);
      if (e.key === "ArrowLeft") setLightboxIndex((prev) => (prev - 1 + filteredImages.length) % filteredImages.length);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxIndex, filteredImages]);

  return (
    <section className="min-h-screen bg-[#0A0A0B] pt-32 pb-24 sm:pb-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-12">
        {/* Back Link */}
        <div className="mb-10 animate-fadeInUp">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/5 hover:border-[#D4AF37]/30 bg-white/5 hover:bg-[#D4AF37]/10 text-gray-300 hover:text-[#D4AF37] font-semibold text-sm transition-all group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span>{t("backToHome")}</span>
          </Link>
        </div>

        {/* Title */}
        <div className="text-center mb-16 animate-fadeInUp delay-100">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37] mb-4">
            {BUSINESS.name}
          </p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl tracking-tight font-black text-white mb-6">
            {t("ourGallery")}
          </h1>
          <p className="text-gray-300 leading-relaxed max-w-2xl mx-auto text-base sm:text-lg">
            {t("gallerySubtitle")}
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center min-h-[300px]">
            <div className="w-12 h-12 rounded-full border-2 border-[#D4AF37] border-t-transparent animate-spin mb-4" />
            <p className="text-gray-400 text-sm">Loading gallery...</p>
          </div>
        ) : images.length === 0 ? (
          <div className="flex flex-col items-center justify-center min-h-[300px] border border-dashed border-white/10 rounded-2xl p-8 bg-white/5">
            <ImageIcon size={48} className="text-gray-600 mb-4" />
            <p className="text-gray-400 font-medium mb-1">No images available</p>
            <p className="text-gray-500 text-sm text-center max-w-sm">Place image files in `public/images/gallery/raw/` and execute the optimization script to populate the gallery.</p>
          </div>
        ) : (
          <>
            {/* Category Pills (rendered only if we have more than 1 category) */}
            {categories.length > 2 && (
              <div className="flex flex-wrap items-center justify-center gap-2.5 mb-12 animate-fadeInUp delay-200">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-5 py-2.5 rounded-full text-xs font-bold uppercase tracking-wider border transition-all duration-300 ${
                      activeCategory === cat
                        ? "bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/60 shadow-[0_0_15px_rgba(212,175,55,0.15)]"
                        : "bg-white/5 text-gray-400 border-white/5 hover:border-white/10 hover:text-white"
                    }`}
                  >
                    {getCategoryLabel(cat)}
                  </button>
                ))}
              </div>
            )}

            {/* Gallery Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 animate-fadeInUp delay-300">
              {filteredImages.map((img, idx) => (
                <div
                  key={img.id}
                  onClick={() => openLightbox(idx)}
                  className="group relative cursor-pointer overflow-hidden rounded-2xl border border-white/5 hover:border-[#D4AF37]/30 transition-all duration-500 shadow-xl bg-black/40 h-[280px]"
                >
                  {/* Dynamic Picture Tag for Responsive Load */}
                  <picture className="w-full h-full">
                    {img.avif && <source srcSet={img.avif} type="image/avif" />}
                    <source srcSet={img.webp} type="image/webp" />
                    <img
                      src={img.jpg}
                      alt={img.title}
                      loading="lazy"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                  </picture>

                  {/* Visual Overlay on Hover */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-5">
                    <p className="text-white font-bold text-lg leading-tight transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                      {img.title}
                    </p>
                    <p className="text-[#D4AF37] text-xs font-semibold uppercase tracking-wider mt-1.5 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300 delay-75">
                      {getCategoryLabel(img.category.toLowerCase())}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && filteredImages[lightboxIndex] && (
        <div
          onClick={closeLightbox}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md transition-all duration-300"
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-gray-400 hover:text-white transition-colors p-2 z-50 focus-ring rounded-full"
            aria-label="Close Lightbox"
          >
            <X size={28} />
          </button>

          {/* Navigation - Prev */}
          <button
            onClick={(e) => navigateLightbox(-1, e)}
            className="absolute left-6 text-gray-400 hover:text-[#D4AF37] hover:bg-white/5 transition-all p-3 rounded-full z-50 focus-ring"
            aria-label="Previous Image"
          >
            <ChevronLeft size={36} />
          </button>

          {/* Center Image Container */}
          <div 
            onClick={(e) => e.stopPropagation()} 
            className="relative max-w-[90vw] max-h-[80vh] flex flex-col items-center"
          >
            <picture className="max-w-full max-h-[75vh] object-contain select-none shadow-2xl rounded-lg">
              {filteredImages[lightboxIndex].avif && (
                <source srcSet={filteredImages[lightboxIndex].avif} type="image/avif" />
              )}
              <source srcSet={filteredImages[lightboxIndex].webp} type="image/webp" />
              <img
                src={filteredImages[lightboxIndex].jpg}
                alt={filteredImages[lightboxIndex].title}
                className="max-w-full max-h-[75vh] object-contain rounded-lg"
              />
            </picture>

            {/* Title / Counter Overlay */}
            <div className="text-center mt-5">
              <h3 className="text-white font-bold text-lg sm:text-xl">
                {filteredImages[lightboxIndex].title}
              </h3>
              <p className="text-gray-400 text-xs sm:text-sm mt-1">
                {lightboxIndex + 1} / {filteredImages.length}
              </p>
            </div>
          </div>

          {/* Navigation - Next */}
          <button
            onClick={(e) => navigateLightbox(1, e)}
            className="absolute right-6 text-gray-400 hover:text-[#D4AF37] hover:bg-white/5 transition-all p-3 rounded-full z-50 focus-ring"
            aria-label="Next Image"
          >
            <ChevronRight size={36} />
          </button>
        </div>
      )}
    </section>
  );
}
